/**
 * The pandit drawer's Jobs tab (admin doc 07, the design's third tab): one timeline of the
 * work behind the quality numbers — each assignment and how it ended, each payout that reached
 * the bank. Pure, so every wording is pinned by a unit test rather than by a screenshot.
 */
import { formatInr } from '../lib/money';
import type { TimelineEvent } from '../components/entities';
import type { PanditJob } from '../lib/api/hooks/pandits';

/** Why an assignment ended early, in the words a reviewer weighing a suspension needs. */
const ENDED: Record<string, { detail: string; state: TimelineEvent['state'] }> = {
  pandit_cancelled: { detail: 'Declined after accepting — replacement dispatched', state: 'error' },
  no_show: { detail: 'Did not arrive — replacement dispatched', state: 'error' },
  quality_pull: { detail: 'Pulled by ops on quality grounds', state: 'error' },
  admin_reassigned: { detail: 'Reassigned by ops', state: 'future' },
  booking_cancelled: { detail: 'Booking cancelled', state: 'future' },
};

const IST = 'Asia/Kolkata';

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { timeZone: IST, weekday: 'long', day: 'numeric', month: 'long' });
}

function dayAndTime(iso: string): string {
  const time = new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit' });
  return `${day(iso)}, ${time}`;
}

function jobEvent(job: PanditJob): TimelineEvent {
  const title = `${job.puja_name ?? 'Puja'} · ${job.city_name ?? '—'}`;
  const ended = job.release_reason ? ENDED[job.release_reason] : undefined;
  if (ended) return { title, timestamp: dayAndTime(job.at), detail: ended.detail, state: ended.state };
  if (job.assignment_status === 'completed') {
    const rated = job.rating ? ` · rated ${job.rating}` : '';
    return { title, timestamp: dayAndTime(job.at), detail: `Completed${rated}`, state: 'done' };
  }
  if (job.assignment_status === 'active') {
    return { title, timestamp: dayAndTime(job.at), detail: 'Assigned', state: 'current' };
  }
  return { title, timestamp: dayAndTime(job.at), detail: job.assignment_status ?? '', state: 'future' };
}

export function jobsTimeline(items: readonly PanditJob[]): TimelineEvent[] {
  return items.map((item) =>
    item.kind === 'payout'
      ? {
          title: 'Payout credited',
          timestamp: day(item.at),
          amount: formatInr(item.amount_paise ?? 0, { paise: true }),
          state: 'money',
        }
      : jobEvent(item),
  );
}
